import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Mail, ArrowUpRight } from "lucide-react";
import { RevealSection, SplitText, EASE } from "../motion/ScrollFX";

// ---------------------------------------------------------------------
// Shared visual system for legal pages. Same tokens as Home/Work so it
// still reads as the same product. The hero leans into what a legal
// page actually *is* — a sealed, numbered, clause-by-clause document —
// rather than a generic dark hero with two static blur circles.
// ---------------------------------------------------------------------
export const legalTheme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  heroBgDeep: "#120A0B",
  darkBandDeep: "#160B0D",
};

export function Eyebrow({ children, light = false }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4"
      style={{ color: light ? "#FF8A93" : legalTheme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : legalTheme.accent }} />
      {children}
    </div>
  );
}

export function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : legalTheme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// Slim fixed bar at the very top of the viewport, fills left-to-right
// with total page scroll. Legal pages are long single-scroll reads —
// this is the one signature device, kept quiet everywhere else.
export function ScrollProgressRail() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 32, restDelta: 0.001 });
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[3px] origin-left z-[70]"
      style={{
        scaleX,
        background: `linear-gradient(90deg, ${legalTheme.accent}, ${legalTheme.gold})`,
      }}
    />
  );
}

// Hook: tracks which section id is currently in view via
// IntersectionObserver — powers both the desktop sidebar highlight and
// the mobile pill strip.
export function useActiveSection(ids) {
  const [activeId, setActiveId] = useState(ids[0]);

  useEffect(() => {
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.length]);

  return activeId;
}

// ================= HERO BACKGROUND FX =================
// Two slow "breathing" aurora blobs instead of static blur circles —
// same palette, now drifting and pulsing on independent loops.
function HeroAurora() {
  return (
    <>
      <motion.div
        className="pointer-events-none absolute top-0 right-0 w-[34rem] h-[34rem] rounded-full blur-3xl"
        style={{ backgroundColor: legalTheme.accent }}
        animate={{
          opacity: [0.12, 0.2, 0.12],
          x: ["28%", "36%", "28%"],
          y: ["-36%", "-27%", "-36%"],
          scale: [1, 1.12, 1],
        }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute bottom-0 left-0 w-[26rem] h-[26rem] rounded-full blur-3xl"
        style={{ backgroundColor: legalTheme.gold }}
        animate={{
          opacity: [0.08, 0.16, 0.08],
          x: ["-34%", "-25%", "-34%"],
          y: ["30%", "38%", "30%"],
          scale: [1, 1.08, 1],
        }}
        transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
      />
    </>
  );
}

// A thin band of light that sweeps across the hero on a slow interval,
// like a scanner passing over a page — reinforces "document being
// read/verified" without being literal about it.
function ScanSweep() {
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none absolute inset-y-0 w-[42%]"
      style={{
        background: `linear-gradient(100deg, transparent, ${legalTheme.gold}14 45%, ${legalTheme.accent}20 52%, transparent 88%)`,
        mixBlendMode: "screen",
      }}
      animate={{ left: ["-50%", "120%"] }}
      transition={{ duration: 6.5, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" }}
    />
  );
}

// Two concentric dashed rings rotating in opposite directions around
// the hero icon — reads as a wax seal / notarised stamp, which is a
// more specific idea for a legal page than a plain icon badge.
function SealRing({ Icon }) {
  return (
    <div className="relative w-14 h-14 shrink-0">
      <motion.svg
        viewBox="0 0 100 100"
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 42, repeat: Infinity, ease: "linear" }}
      >
        <circle cx="50" cy="50" r="47" fill="none" stroke={legalTheme.gold} strokeWidth="1.4" strokeDasharray="4 7" opacity="0.55" />
      </motion.svg>
      <motion.svg
        viewBox="0 0 100 100"
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: -360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <circle cx="50" cy="50" r="38" fill="none" stroke={legalTheme.accent} strokeWidth="1" strokeDasharray="1 5" opacity="0.5" />
      </motion.svg>
      <motion.span
        initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
        className="absolute inset-[8px] rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: "rgba(230,57,70,0.14)", border: "1px solid rgba(230,57,70,0.25)" }}
      >
        <Icon size={21} style={{ color: "#FF8A93" }} />
      </motion.span>
    </div>
  );
}

// Small clause-reference tags drifting upward and fading — a nod to
// the numbered sections that make up the actual page below, so the
// hero motion is about this content rather than generic ambience.
const clauseTags = ["§ 4.2", "Art. IX", "cl. 7(b)", "§ 1.1", "Sched. B", "cl. 12", "§ 9(a)", "Art. III"];

function FloatingClauses() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {clauseTags.map((tag, i) => {
        const left = 6 + ((i * 137) % 92);
        const duration = 9 + (i % 4) * 2.5;
        const delay = (i * 1.3) % 6;
        return (
          <motion.span
            key={tag}
            className="absolute font-mono text-[11px] tracking-wide rounded-full px-2.5 py-1 border whitespace-nowrap"
            style={{
              left: `${left}%`,
              bottom: "-8%",
              color: "rgba(255,255,255,0.42)",
              borderColor: "rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.03)",
            }}
            animate={{ y: ["0%", "-680%"], opacity: [0, 0.7, 0.7, 0] }}
            transition={{ duration, repeat: Infinity, delay, ease: "linear", times: [0, 0.15, 0.85, 1] }}
          >
            {tag}
          </motion.span>
        );
      })}
    </div>
  );
}

// ================= HERO =================
export function LegalHero({ eyebrow, title, HeroIcon, meta, description, agreementNote }) {
  return (
    <section
      className="relative overflow-hidden pt-40 pb-20 md:pt-48 md:pb-24"
      style={{ background: `radial-gradient(ellipse 90% 60% at 50% 0%, #2A1216 0%, ${legalTheme.heroBg} 55%, ${legalTheme.heroBgDeep} 100%)` }}
    >
      <DotGrid light />
      <HeroAurora />
      <ScanSweep />
      <FloatingClauses />

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <div className="flex items-center gap-4 mb-2">
            <SealRing Icon={HeroIcon} />
            <Eyebrow light>{eyebrow}</Eyebrow>
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.08] tracking-tight text-white">
            <SplitText text={title[0]} /> <SplitText text={title[1]} delay={0.15} />
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/50">{meta}</div>

          <p className="mt-6 text-lg text-white/60 max-w-2xl leading-relaxed">{description}</p>
          <p className="mt-4 text-sm text-white/40">{agreementNote}</p>
        </motion.div>
      </div>
    </section>
  );
}

// ================= DESKTOP SIDEBAR NAV =================
export function SectionNav({ sections, activeId, contactNo }) {
  const activeIndex = sections.findIndex((s) => s.id === activeId);
  const progress = activeId === "contact-us" ? 1 : Math.max(0, activeIndex) / sections.length;

  return (
    <nav className="hidden lg:block sticky top-28 self-start">
      <span className="block text-[11px] font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: legalTheme.inkMuted }}>
        On this page
      </span>
      <div className="relative pl-4">
        {/* track */}
        <span className="absolute left-0 top-0 bottom-0 w-[2px] rounded-full" style={{ backgroundColor: legalTheme.border }} />
        {/* animated fill */}
        <motion.span
          className="absolute left-0 top-0 w-[2px] rounded-full origin-top"
          style={{ backgroundColor: legalTheme.accent, height: "100%" }}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: progress }}
          transition={{ duration: 0.4, ease: EASE }}
        />
        <ul className="space-y-1 max-h-[calc(100vh-10rem)] overflow-y-auto pr-2">
          {sections.map((s) => {
            const active = activeId === s.id;
            const Icon = s.icon;
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-cursor="hover"
                  className="group flex items-center gap-2.5 pr-2 py-1.5 transition-colors duration-200"
                  style={{ color: active ? legalTheme.ink : legalTheme.inkMuted }}
                >
                  <Icon size={13} className="shrink-0 transition-colors duration-200" style={{ color: active ? legalTheme.accent : "rgba(107,98,101,0.5)" }} />
                  <span className={`text-sm leading-snug ${active ? "font-semibold" : "font-medium"}`}>{s.title}</span>
                </a>
              </li>
            );
          })}
          <li>
            <a
              href="#contact-us"
              data-cursor="hover"
              className="flex items-center gap-2.5 pr-2 py-1.5 transition-colors duration-200"
              style={{ color: activeId === "contact-us" ? legalTheme.ink : legalTheme.inkMuted }}
            >
              <Mail size={13} className="shrink-0" style={{ color: activeId === "contact-us" ? legalTheme.accent : "rgba(107,98,101,0.5)" }} />
              <span className={`text-sm leading-snug ${activeId === "contact-us" ? "font-semibold" : "font-medium"}`}>Contact Us</span>
            </a>
          </li>
        </ul>
      </div>
    </nav>
  );
}

// ================= MOBILE PILL STRIP =================
export function MobileSectionPills({ sections, activeId }) {
  return (
    <div
      className="lg:hidden sticky top-[64px] z-30 -mx-6 px-6 py-3 flex gap-2 overflow-x-auto"
      style={{ backgroundColor: `${legalTheme.surface}F2`, backdropFilter: "blur(8px)", borderBottom: `1px solid ${legalTheme.border}` }}
    >
      {sections.map((s) => {
        const active = activeId === s.id;
        const Icon = s.icon;
        return (
          <a
            key={s.id}
            href={`#${s.id}`}
            data-cursor="hover"
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors duration-200 border"
            style={{
              color: active ? "#FFFFFF" : legalTheme.inkMuted,
              backgroundColor: active ? legalTheme.accent : "transparent",
              borderColor: active ? legalTheme.accent : legalTheme.border,
            }}
          >
            <Icon size={12} />
            {s.title}
          </a>
        );
      })}
    </div>
  );
}

// ================= SECTION CARD =================
export function LegalSection({ section }) {
  const Icon = section.icon;
  const accentColor = section.no % 2 === 0 ? legalTheme.gold : legalTheme.accent;

  return (
    <RevealSection id={section.id} className="scroll-mt-36">
      <div
        className="group relative rounded-2xl border p-6 md:p-8 mb-6 transition-colors duration-300 hover:bg-[#FBF6F3]"
        style={{ borderColor: legalTheme.border, backgroundColor: "#FFFFFF" }}
      >
        <div className="flex items-start gap-4">
          <span
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
            style={{ backgroundColor: `${accentColor}1A`, color: accentColor }}
          >
            <Icon size={19} />
          </span>
          <div className="min-w-0 flex-1 pt-1.5">
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-xs font-semibold" style={{ color: accentColor }}>
                {String(section.no).padStart(2, "0")}
              </span>
              <h2 className="text-xl md:text-2xl font-bold" style={{ color: legalTheme.ink }}>
                {section.title}
              </h2>
            </div>

            {section.intro && (
              <p className="mt-3 leading-relaxed" style={{ color: legalTheme.inkMuted }}>
                {section.intro}
              </p>
            )}

            {section.paragraphs?.map((p, i) => (
              <p key={i} className="mt-3 leading-relaxed" style={{ color: legalTheme.inkMuted }}>
                {p}
              </p>
            ))}

            {section.lead && (
              <p className="mt-3 leading-relaxed" style={{ color: legalTheme.inkMuted }}>
                {section.lead}
              </p>
            )}

            {section.items && (
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {section.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 leading-relaxed text-sm" style={{ color: legalTheme.inkMuted }}>
                    <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: accentColor }} />
                    {item}
                  </li>
                ))}
              </ul>
            )}

            {section.blocks?.map((block, i) => (
              <div key={i} className={`${i > 0 ? "mt-6 pt-6 border-t" : "mt-6"}`} style={{ borderColor: legalTheme.border }}>
                <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: legalTheme.ink }}>
                  {block.heading}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: legalTheme.inkMuted }}>
                  {block.lead}
                </p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 leading-relaxed text-sm" style={{ color: legalTheme.inkMuted }}>
                      <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: accentColor }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {section.note && (
              <p
                className="mt-4 text-sm leading-relaxed pl-3.5 border-l-2"
                style={{ color: legalTheme.inkMuted, borderColor: accentColor }}
              >
                {section.note}
              </p>
            )}
          </div>
        </div>
      </div>
    </RevealSection>
  );
}

// ================= CONTACT BLOCK =================
export function ContactSection({ no, intro, cards }) {
  return (
    <RevealSection id="contact-us" className="scroll-mt-36">
      <div className="rounded-2xl border p-6 md:p-8" style={{ borderColor: legalTheme.border, backgroundColor: legalTheme.surfaceAlt }}>
        <div className="flex items-start gap-4">
          <span
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${legalTheme.accent}1A`, color: legalTheme.accent }}
          >
            <Mail size={19} />
          </span>
          <div className="min-w-0 w-full pt-1.5">
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-xs font-semibold" style={{ color: legalTheme.accent }}>
                {String(no).padStart(2, "0")}
              </span>
              <h2 className="text-xl md:text-2xl font-bold" style={{ color: legalTheme.ink }}>
                Contact Us
              </h2>
            </div>
            <p className="mt-3 leading-relaxed" style={{ color: legalTheme.inkMuted }}>
              {intro}
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {cards.map((c) => (
                <a
                  key={c.email}
                  href={`mailto:${c.email}`}
                  data-cursor="hover"
                  className="group relative rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                  style={{ borderColor: legalTheme.border, backgroundColor: "#FFFFFF" }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${legalTheme.accent}1A`, color: legalTheme.accent }}
                  >
                    <Mail size={17} />
                  </div>
                  <h3 className="font-semibold" style={{ color: legalTheme.ink }}>
                    {c.label}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed" style={{ color: legalTheme.inkMuted }}>
                    {c.desc}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: legalTheme.accent }}>
                    {c.email}
                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              ))}
            </div>

            <p className="mt-6 text-sm" style={{ color: legalTheme.inkMuted }}>
              Website:{" "}
              <a href="https://www.unibcomp.net" target="_blank" rel="noopener noreferrer" data-cursor="hover" className="font-medium" style={{ color: legalTheme.accent }}>
                www.unibcomp.net
              </a>
            </p>
          </div>
        </div>
      </div>
    </RevealSection>
  );
}